import { useContext } from 'react';
import { cn } from '@/utils/cn';
import { DeviceFrameDensityContext, PreviewFluidContext } from '../components/deviceFrameStyle';
import { PlatformPreviewProps } from '../types';
import { PLATFORM_CONFIG, truncateForPlatform } from '../platformConfig';
import { PreviewAvatar } from '../components/PreviewAvatar';
import { PreviewCaption } from '../components/PreviewCaption';
import { EmptyState } from '../components/EmptyState';
import { BlurFillImage } from '../components/BlurFillImage';
import { PlatformBrandIcon } from '../components/PlatformBrandIcon';
import { selectGbpMedia } from '../previewMedia';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

/** Google CTA type -> button text as Google shows it in the UI language (locale keys, adjustable) */
const ctaLabelFor = (t: TFunction, ctaType: string | undefined): string | null => {
  switch (ctaType) {
    case 'BOOK': return t('preview_gbp_cta_book');
    case 'ORDER': return t('preview_gbp_cta_order');
    case 'SHOP': return t('preview_gbp_cta_shop');
    case 'LEARN_MORE': return t('preview_gbp_cta_learn_more');
    case 'SIGN_UP': return t('preview_gbp_cta_sign_up');
    case 'GET_OFFER': return t('preview_gbp_cta_get_offer');
    case 'CALL': return t('preview_gbp_cta_call');
    default: return null;
  }
};

export const GoogleBusinessPreview = ({ caption, media, channel }: PlatformPreviewProps) => {
  const config = PLATFORM_CONFIG.google_my_business;
  const slim = useContext(DeviceFrameDensityContext) === 'slim';
  const fluid = useContext(PreviewFluidContext);
  const { t } = useTranslation('common');
  const displayCaption = truncateForPlatform(caption, 'google_my_business');
  const isEmpty = !caption && media.length === 0;

  const displayName = channel?.displayName ?? '';

  if (isEmpty) return <EmptyState />;

  const selected = selectGbpMedia(media);
  const ctaType = channel?.options?.googleMyBusiness?.ctaType;
  const ctaLabel = ctaLabelFor(t, ctaType);

  return (
    <div className={cn('bg-white rounded-xl border border-gray-200 w-full min-w-0 mx-auto overflow-hidden', fluid ? 'max-w-none' : 'max-w-[400px]', slim ? 'shadow-sm' : 'shadow-md')}>
      {/* Header */}
      <div className="px-3 py-2.5 flex items-center gap-2.5">
        <PreviewAvatar colorClass={config.avatarColor} size="md" avatarUrl={channel?.avatarUrl} initials={channel?.initials} />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-gray-900 text-sm truncate">{displayName}</p>
          <p className="text-xs text-gray-500">{t('preview_gbp_update_label')}</p>
        </div>
        <PlatformBrandIcon platform="google_my_business" className="text-sm text-[#4285F4]" />
      </div>

      {/* Media: Google posts cannot include video. Selection mirrors the backend
          publish path (selectGbpMedia): first image, else the video's cover image. */}
      {selected.url && (
        <BlurFillImage src={selected.url} alt={t('preview_gbp_post_alt')} className="aspect-[4/3] w-full" />
      )}
      {selected.hasVideo && selected.kind !== 'image' && (
        <p className="px-3 py-1.5 text-xs text-gray-600 bg-gray-50 border-b border-gray-100">
          {selected.kind === 'video-thumbnail'
            ? t('preview_gbp_video_cover_notice', { defaultValue: "Google posts can't include video · the video's cover image is used" })
            : t('preview_gbp_video_text_only_notice', { defaultValue: "Google posts can't include video · posted as text only" })}
        </p>
      )}

      {/* Content */}
      {caption && (
        <div className="px-3 pt-2.5">
          <PreviewCaption
            caption={displayCaption}
            hashtagColor={config.hashtagColor}
            className="text-sm leading-5 text-gray-900"
            truncated={caption.length > 150}
          />
        </div>
      )}

      {/* Call to action: only when the post really has one */}
      {ctaLabel ? (
        <div className="px-3 py-3">
          <span className="inline-block rounded-full border border-gray-300 px-4 py-1.5 text-sm font-medium text-[#1A73E8]">
            {ctaLabel}
          </span>
        </div>
      ) : (
        <div className="h-2.5" />
      )}
    </div>
  );
};
